import React, { useEffect, useState } from "react";
import { arrayOf, bool, func, oneOf, string } from "prop-types";

import {
  cmsPlacementsPropType,
  segmentTargetsPropTypes
} from "../../constants/cmsPropTypes";
import { sendExperimentId } from "../../utilities/sendExperimentId";
import CMSBanner from "../CMSBanner";

/**
 * ContentManagementSystem renders a target for a particular placement.
 * It can be used via the rails `se_embed_cms` helper,
 * or directly by another React component.
 */
const ContentManagementSystem = ({
  cmsPlacements,
  defaultTarget,
  handleSetTarget,
  isHiddenSeparator,
  location,
  userSegments,
  ...passThroughProps
}) => {
  const [target, setTarget] = useState(defaultTarget);

  const getRandomTarget = targets =>
    targets[Math.floor(Math.random() * targets.length)];

  const getLocationSegments = placements => {
    if (!placements || placements?.length === 0) return null;

    const placement = placements.find(p => p.machine_name === location);

    return placement ? placement.segment_targets : null;
  };

  const setAnalytics = (
    currentCreative,
    segmentTargetId,
    segmentTargetExperimentId
  ) => {
    window.dataLayer[0].segmentID = userSegments.join();
    window.dataLayer.push({
      event: "custom_event",
      event_category: "content_banners",
      event_action: "impression",
      event_label: `creative_id: ${
        currentCreative?.id
      }|segment_target_id: ${segmentTargetId}${sendExperimentId(
        segmentTargetExperimentId
      )}`
    });
  };

  const hasTarget = selectedTarget => selectedTarget?.creatives.length > 0;

  const setTargetAndAnalytics = selectedTarget => {
    handleSetTarget(hasTarget(selectedTarget));
    setTarget(selectedTarget);
    if (selectedTarget) {
      setAnalytics(
        selectedTarget.creatives[0],
        selectedTarget.segment_target_id,
        selectedTarget.segment_target_experiment_id
      );
    }
  };

  useEffect(() => {
    const segmentTargets = getLocationSegments(cmsPlacements);

    if (segmentTargets) {
      const randomTarget = getRandomTarget(segmentTargets);
      // Prefer server picked target over the client one
      setTargetAndAnalytics(target || randomTarget);
    } else {
      handleSetTarget(false);
    }
  }, []);

  return (
    <>
      {hasTarget(target) > 0 && (
        <CMSBanner
          altText={target.creatives[0].alt_text}
          creativeId={target.creatives[0].id}
          creativeMaxHeight={target.creatives[0].max_height}
          creativeMinHeight={target.creatives[0].min_height}
          creativeWidth={target.creatives[0].width}
          imageUrl={target.creatives[0].image_url}
          isHiddenSeparator={isHiddenSeparator}
          segmentTargetExperimentId={target?.segment_target_experiment_id}
          segmentTargetId={target.segment_target_id}
          url={target.url}
          {...passThroughProps}
        />
      )}
    </>
  );
};

ContentManagementSystem.propTypes = {
  cmsPlacements: cmsPlacementsPropType.isRequired,
  handleSetTarget: func,
  isHiddenSeparator: bool,
  location: oneOf(["body", "header", "propertiesTab"]).isRequired,
  /**
   * Optionally allows the target to be selected and rendered server-side.
   */
  defaultTarget: segmentTargetsPropTypes,
  userSegments: arrayOf(string).isRequired
};

ContentManagementSystem.defaultProps = {
  handleSetTarget: () => {},
  isHiddenSeparator: false,
  defaultTarget: null
};

export default ContentManagementSystem;
